import React, { useState, useEffect } from 'react';
import { MdArrowRightAlt } from "react-icons/md";
import { create, all } from 'mathjs';
import 'katex/dist/katex.min.css';
import renderMathInElement from "katex/dist/contrib/auto-render.js";
import './index.css';

const math = create(all);

function ScientificCalculator() {
    const [mode, setMode] = useState<'digits'|'functions'>('digits');
    const [degreeMode, setDegreeMode] = useState<'Rad'|'Deg'>('Rad');
    const [shift, setShift] = useState(false);
    const [value, setValueHelper] = useState('0');
    const [operator, setOperator] = useState<'×'|'÷'|'+'|'-'|'^'|'root'|'='|'('|null>(null);
    const [preview, setPreview] = useState('');
    const [lastTerm, setLastTerm] = useState('');
    const [lastInputType, setLastInputType] = useState<'value'|'constant'|'operator'|'function'>('value');
    const [memory, setMemory] = useState('0');
    const [history, setHistory] = useState<{'preview': string, 'answer': string}[]>([]);
    const [historyOpen, setHistoryOpen] = useState(false);

    const setValue = (value: any) => {
        if (math.isComplex(value) && value.im !== 0) {
            setValueHelper('Error');
        }
        else {
            setValueHelper(String(value));
        }
    }

    const memoryClear = () => {
        setMemory('0');
    }

    const memoryAdd = () => {
        setMemory(prev => String(math.evaluate(`${prev}+${value}`)))
    }

    const memorySubtract = () => {
        setMemory(prev => String(math.evaluate(`${prev}-${value}`)));
    }

    const memoryRecall = () => {
        if (operator === '=') {
            clearAll();
        }
        setValue(memory);
        setLastInputType('value');
    }

    const backSpace = () => {
        if (lastInputType === 'value' && operator !== '=') {
            if (value.length <= 1) {
                setValue('0');
            }
            else {
                setValue(value.slice(0, -1));
            }
        }
    }

    const clearAll = () => {
        setValue('0');
        setOperator(null);
        setPreview('');
        setLastTerm('');
        setLastInputType('value');
    }
    
    const clearEntry = () => {
        if (operator === '=') {
            clearAll();
        }
        else {
            if (lastTerm) {
                removeLastTerm();
                setLastInputType('operator');
            }
            setValue('0');
        }
    }

    const removeLastTerm = () => {
        const lastIndex = preview.lastIndexOf(lastTerm);
        setPreview(preview.slice(0, lastIndex));
        setLastTerm('');
    }

    const replaceLastTerm = (newTerm: string) => {
        const lastIndex = preview.lastIndexOf(lastTerm);
        setPreview(preview.slice(0, lastIndex) + newTerm);
        setLastTerm(newTerm);
    }

    const getLastGroup = (operation: string) => {
        let lastGroup = '';
        let count = 0;
        for (let i = operation.length - 1; i >= 0; i--) {
            lastGroup = operation[i] + lastGroup;
            if (operation[i] === '(') count -= 1;
            else if (operation[i] === ')') count += 1;

            if (count === 0) break;
        }
        return lastGroup;
    }

    const addValue = (input: string) => {
        if (operator === '=') {
            clearAll();
        }
        else if (lastInputType === 'function') {
            removeLastTerm();
        }

        if (lastInputType === 'operator' || lastInputType === 'function' || lastInputType === 'constant') {
            setValue(input === '.' ? '0.' : input);
        }
        else if (lastInputType === 'value') {
            if (input === '.' && value.includes('.')) {
                return;
            }
            else if (input === '.' && value === '0') {
                setValue('0.');
            }
            else {
                setValue(value === '0' ? input : value + input); 
            }
        }
        setLastTerm('');
        setLastInputType('value');
    }

    const addOperator = (input: '×'|'÷'|'+'|'-'|'^'|'root') => {
        if (operator === '=') {
            if (input === 'root') setPreview(prev => `root((${prev}), )`)
            else setPreview(prev => `(${prev})` + ` ${input} `);
        }
        else if (lastTerm === '(' || (operator === 'root' && lastInputType === 'operator')) {
            return;
        }
        else if (operator === 'root' && lastInputType === 'value') {
            if (input === 'root') {
                setPreview(prev => `root(${prev.slice(0, -1)}${value}), )`);
            }
            else {
                setPreview(prev => prev.slice(0, -1) + value + ')' + ` ${input} `);
            }
        }
        else if (lastInputType === 'operator') {
            if (input === 'root') setPreview(prev => `root(${prev.slice(0, -3)}, )`)
            else setPreview(prev => prev.slice(0, -3) + ` ${input} `);
        }
        else if (lastInputType === 'value') {
            if (input === 'root') replaceLastTerm(`root(${value.at(-1) === '.' ? value.slice(0, -1) : value}, )`);
            else setPreview(prev => prev + (value.at(-1) === '.' ? value.slice(0, -1) : value) + ` ${input} `);
        }
        else if (lastInputType === 'function' || lastInputType === 'constant') {
            if (input === 'root') replaceLastTerm(`root(${lastTerm}, )`);
            else setPreview(prev => prev + ` ${input} `);
        }
        setValue('0');
        setOperator(input);
        setLastTerm('');
        setLastInputType('operator');
    }

    const handleFunction = (input: string) => {
        if (operator === 'root') return;
         
        switch(input) {
            case '±':
                if (operator === '=' ||
                    lastInputType === 'function' ||
                    // lastInputType === 'constant' ||
                    value === '0'
                ) {
                    break;
                }
                else if (lastInputType === 'constant') {
                    replaceLastTerm(lastTerm[0] === '-' ? lastTerm.slice(1) : `-${lastTerm}`);
                }
                setValue(value[0] === '-' ? value.slice(1) : `-${value}`);

                break;
            case 'Abs':
                if (operator === '=') {
                    setPreview(prev => '|(' + prev + ')|');
                }
                else if (lastInputType === 'function') {
                    replaceLastTerm(`|(${lastTerm})|`);
                }
                else if (lastInputType === 'constant') {
                    replaceLastTerm(`|${lastTerm}|`);
                }
                else if (lastInputType === 'value') {
                    setPreview(prev => prev + `|${value}|`);
                    setLastTerm(`|${value}|`);
                }
                else {
                    break;
                }
                setValue(math.evaluate(`abs(${value})`));
                setLastInputType('function');
                break;
            case '%':
                if (operator === '=') {
                    setPreview(prev => '(' + prev + ')%');
                }
                else if (lastInputType === 'function') {
                    replaceLastTerm(`(${lastTerm})%`);
                }
                else if (lastInputType === 'constant') {
                    replaceLastTerm(`${lastTerm}%`);
                }
                else if (lastInputType === 'value') {
                    setPreview(prev => prev + `${value}%`);
                    setLastTerm(`${value}%`);
                }
                else {
                    break;
                }
                setValue(math.evaluate(`${value}/100`));
                setLastInputType('function');
                break;
            case '(':
                if (operator === '=') {
                    clearAll();
                }

                if (lastInputType === 'function' || lastInputType === 'constant') {
                    replaceLastTerm('(');
                }
                else if (lastInputType === 'value' || lastInputType === 'operator') {
                    setLastTerm('(');
                    setPreview(prev => prev + '(');
                }
                setValue('0');
                setOperator('(');
                setLastInputType('operator');
                break;
            case ')':
                if (operator === '=' ||
                    lastInputType === 'operator' || 
                    preview.split('(').length <= preview.split(')').length
                ) {
                    break;
                }

                let newPreview = '';
                if (lastInputType === 'value') {
                    newPreview = preview + (value.at(-1) === '.' ? value.slice(0, -1) : value) + ')';
                }
                else if (lastInputType === 'function' || lastInputType === 'constant') {
                    newPreview = preview + ')';
                }
                const lastGroup = getLastGroup(newPreview);
                setPreview(newPreview);
                setOperator(null);
                setValue(math.evaluate(mapOperation(lastGroup)));
                setLastTerm(lastGroup);
                setLastInputType('function');
                break;
            case '2^x':
                if (operator === '=') {
                    setPreview(prev => '2 ^ (' + prev + ')');
                }
                else if (lastInputType === 'function') {
                    replaceLastTerm(`2 ^ (${lastTerm})`);
                }
                else if (lastInputType === 'constant') {
                    replaceLastTerm(`2 ^ ${lastTerm}`);
                }
                else if (lastInputType === 'value') {
                    setPreview(prev => prev + `2 ^ ${value}`);
                    setLastTerm(`2 ^ ${value}`);
                }
                else {
                    break;
                }
                setValue(math.evaluate(`2^${value}`));
                setLastInputType('function');
                break;
            case '10^x':
                if (operator === '=') {
                    setPreview(prev => '10 ^ (' + prev + ')');
                }
                else if (lastInputType === 'function') {
                    replaceLastTerm(`10 ^ (${lastTerm})`);
                }
                else if (lastInputType === 'constant') {
                    replaceLastTerm(`10 ^ ${lastTerm}`);
                }
                else if (lastInputType === 'value') {
                    setPreview(prev => prev + `10 ^ ${value}`);
                    setLastTerm(`10 ^ ${value}`);
                }
                else {
                    break;
                }
                setValue(math.evaluate(`10^${value}`));
                setLastInputType('function');
                break;
            case 'x^2':
                if (operator === '=') {
                    setPreview(prev => '(' + prev + ') ^ 2');
                }
                else if (lastInputType === 'function') {
                    replaceLastTerm(`(${lastTerm}) ^ 2`);
                }
                else if (lastInputType === 'constant') {
                    replaceLastTerm(`${lastTerm} ^ 2`);
                }
                else if (lastInputType === 'value') {
                    setPreview(prev => prev + `${value} ^ 2`);
                    setLastTerm(`${value} ^ 2`);
                }
                else {
                    break;
                }
                setValue(math.evaluate(`${value}^2`));
                setLastInputType('function');
                break;
            case 'x^3':
                    if (operator === '=') {
                        setPreview(prev => '(' + prev + ') ^ 3');
                    }
                    else if (lastInputType === 'function') {
                        replaceLastTerm(`(${lastTerm}) ^ 3`);
                    }
                    else if (lastInputType === 'constant') {
                        replaceLastTerm(`${lastTerm} ^ 3`);
                    }
                    else if (lastInputType === 'value') {
                        setPreview(prev => prev + `${value} ^ 3`);
                        setLastTerm(`${value} ^ 3`);
                    }
                    else {
                        break;
                    }
                    setValue(math.evaluate(`${value}^3`));
                    setLastInputType('function');
                    break;
            case '1/x':
                if (operator === '=') {
                    setPreview(prev => '1 / (' + prev + ')');
                }
                else if (lastInputType === 'function') {
                    replaceLastTerm(`1 / (${lastTerm})`);
                }
                else if (lastInputType === 'constant') {
                    replaceLastTerm(`1 / ${lastTerm}`);
                }
                else if (lastInputType === 'value') {
                    setPreview(prev => prev + `1 / ${value}`);
                    setLastTerm(`1 / ${value}`);
                }
                else {
                    break;
                }
                setValue(math.evaluate(`1/${value}`));
                setLastInputType('function');
                break;
            case 'x!':
                if (operator === '=') {
                    setPreview(prev => '(' + prev + ')!');
                }
                else if (lastInputType === 'function') {
                    replaceLastTerm(`(${lastTerm})!`);
                }
                else if (lastInputType === 'constant') {
                    replaceLastTerm(`${lastTerm}!`);
                }
                else if (lastInputType === 'value') {
                    setPreview(prev => prev + `${value}!`);
                    setLastTerm(`${value}!`);
                }
                else {
                    break;
                }
                setValue(math.evaluate(`${value}!`));
                setLastInputType('function');
                break;
            case 'sqrt':
                if (operator === '=') {
                    setPreview(prev => 'sqrt(' + prev + ')');
                }
                else if (lastInputType === 'function' || lastInputType === 'constant') {
                    replaceLastTerm(`sqrt(${lastTerm})`);
                }
                else if (lastInputType === 'value') {
                    setPreview(prev => prev + `sqrt(${value})`);
                    setLastTerm(`sqrt(${value})`);
                }
                else {
                    break;
                }
                setValue(math.evaluate(`sqrt(${value})`));
                setLastInputType('function');
                break;
            case 'cbrt':
                if (operator === '=') {
                    setPreview(prev => 'cbrt(' + prev + ')');
                }
                else if (lastInputType === 'function' || lastInputType === 'constant') {
                    replaceLastTerm(`cbrt(${lastTerm})`);
                }
                else if (lastInputType === 'value') {
                    setPreview(prev => prev + `cbrt(${value})`);
                    setLastTerm(`cbrt(${value})`);
                }
                else {
                    break;
                }
                setValue(math.evaluate(`cbrt(${value})`));
                setLastInputType('function');
                break;
            case 'sin':
                var rad = degreeMode === 'Rad' ? value : value + 'deg';
                if (operator === '=') {
                    setPreview(prev => 'sin(' + prev + ')');
                }
                else if (lastInputType === 'function' || lastInputType === 'constant') {
                    replaceLastTerm(`sin(${lastTerm})`);
                }
                else if (lastInputType === 'value') {
                    setPreview(prev => prev + `sin(${value})`);
                    setLastTerm(`sin(${value})`);
                }
                else {
                    break;
                }
                setValue(math.evaluate(`sin(${rad})`));
                setLastInputType('function');
                break;
            case 'asin':
                var rad = degreeMode === 'Rad' ? value : value + 'deg';
                if (operator === '=') {
                    setPreview(prev => 'asin(' + prev + ')');
                }
                else if (lastInputType === 'function' || lastInputType === 'constant') {
                    replaceLastTerm(`asin(${lastTerm})`);
                }
                else if (lastInputType === 'value') {
                    setPreview(prev => prev + `asin(${value})`);
                    setLastTerm(`asin(${value})`);
                }
                else {
                    break;
                }
                setValue(math.evaluate(`asin(${rad})`));
                setLastInputType('function');
                break;
            case 'cos':
                var rad = degreeMode === 'Rad' ? value : value + 'deg';
                if (operator === '=') {
                    setPreview(prev => 'cos(' + prev + ')');
                }
                else if (lastInputType === 'function' || lastInputType === 'constant') {
                    replaceLastTerm(`cos(${lastTerm})`);
                }
                else if (lastInputType === 'value') {
                    setPreview(prev => prev + `cos(${value})`);
                    setLastTerm(`cos(${value})`);
                }
                else {
                    break;
                }
                setValue(math.evaluate(`cos(${rad})`));
                setLastInputType('function');
                break;
            case 'acos':
                var rad = degreeMode === 'Rad' ? value : value + 'deg';
                if (operator === '=') {
                    setPreview(prev => 'acos(' + prev + ')');
                }
                else if (lastInputType === 'function' || lastInputType === 'constant') {
                    replaceLastTerm(`acos(${lastTerm})`);
                }
                else if (lastInputType === 'value') {
                    setPreview(prev => prev + `acos(${value})`);
                    setLastTerm(`acos(${value})`);
                }
                else {
                    break;
                }
                setValue(math.evaluate(`acos(${rad})`));
                setLastInputType('function');
                break;
            case 'tan':
                var rad = degreeMode === 'Rad' ? value : value + 'deg';
                if (operator === '=') {
                    setPreview(prev => 'tan(' + prev + ')');
                }
                else if (lastInputType === 'function' || lastInputType === 'constant') {
                    replaceLastTerm(`tan(${lastTerm})`);
                }
                else if (lastInputType === 'value') {
                    setPreview(prev => prev + `tan(${value})`);
                    setLastTerm(`tan(${value})`);
                }
                else {
                    break;
                }
                setValue(math.evaluate(`tan(${rad})`));
                setLastInputType('function');
                break;
            case 'atan':
                var rad = degreeMode === 'Rad' ? value : value + 'deg';
                if (operator === '=') {
                    setPreview(prev => 'atan(' + prev + ')');
                }
                else if (lastInputType === 'function' || lastInputType === 'constant') {
                    replaceLastTerm(`atan(${lastTerm})`);
                }
                else if (lastInputType === 'value') {
                    setPreview(prev => prev + `atan(${value})`);
                    setLastTerm(`atan(${value})`);
                }
                else {
                    break;
                }
                setValue(math.evaluate(`atan(${rad})`));
                setLastInputType('function');
                break;
            case 'sinh':
                if (operator === '=') {
                    setPreview(prev => 'sinh(' + prev + ')');
                }
                else if (lastInputType === 'function' || lastInputType === 'constant') {
                    replaceLastTerm(`sinh(${lastTerm})`);
                }
                else if (lastInputType === 'value') {
                    setPreview(prev => prev + `sinh(${value})`);
                    setLastTerm(`sinh(${value})`);
                }
                else {
                    break;
                }
                setValue(math.evaluate(`sinh(${value})`));
                setLastInputType('function');
                break;
            case 'asinh':
                if (operator === '=') {
                    setPreview(prev => 'asinh(' + prev + ')');
                }
                else if (lastInputType === 'function' || lastInputType === 'constant') {
                    replaceLastTerm(`asinh(${lastTerm})`);
                }
                else if (lastInputType === 'value') {
                    setPreview(prev => prev + `asinh(${value})`);
                    setLastTerm(`asinh(${value})`);
                }
                else {
                    break;
                }
                setValue(math.evaluate(`asinh(${value})`));
                setLastInputType('function');
                break;
            case 'cosh':
                if (operator === '=') {
                    setPreview(prev => 'cosh(' + prev + ')');
                }
                else if (lastInputType === 'function' || lastInputType === 'constant') {
                    replaceLastTerm(`cosh(${lastTerm})`);
                }
                else if (lastInputType === 'value') {
                    setPreview(prev => prev + `cosh(${value})`);
                    setLastTerm(`cosh(${value})`);
                }
                else {
                    break;
                }
                setValue(math.evaluate(`cosh(${value})`));
                setLastInputType('function');
                break;
            case 'acosh':
                if (operator === '=') {
                    setPreview(prev => 'acosh(' + prev + ')');
                }
                else if (lastInputType === 'function' || lastInputType === 'constant') {
                    replaceLastTerm(`acosh(${lastTerm})`);
                }
                else if (lastInputType === 'value') {
                    setPreview(prev => prev + `acosh(${value})`);
                    setLastTerm(`acosh(${value})`);
                }
                else {
                    break;
                }
                setValue(math.evaluate(`acosh(${value})`));
                setLastInputType('function');
                break;
            case 'tanh':
                if (operator === '=') {
                    setPreview(prev => 'tanh(' + prev + ')');
                }
                else if (lastInputType === 'function' || lastInputType === 'constant') {
                    replaceLastTerm(`tanh(${lastTerm})`);
                }
                else if (lastInputType === 'value') {
                    setPreview(prev => prev + `tanh(${value})`);
                    setLastTerm(`tanh(${value})`);
                }
                else {
                    break;
                }
                setValue(math.evaluate(`tanh(${value})`));
                setLastInputType('function');
                break;
            case 'atanh':
                if (operator === '=') {
                    setPreview(prev => 'atanh(' + prev + ')');
                }
                else if (lastInputType === 'function' || lastInputType === 'constant') {
                    replaceLastTerm(`atanh(${lastTerm})`);
                }
                else if (lastInputType === 'value') {
                    setPreview(prev => prev + `atanh(${value})`);
                    setLastTerm(`atanh(${value})`);
                }
                else {
                    break;
                }
                setValue(math.evaluate(`atanh(${value})`));
                setLastInputType('function');
                break;
            case 'log2':
                if (operator === '=') {
                    setPreview(prev => 'log2(' + prev + ')');
                }
                else if (lastInputType === 'function' || lastInputType === 'constant') {
                    replaceLastTerm(`log2(${lastTerm})`);
                }
                else if (lastInputType === 'value') {
                    setPreview(prev => prev + `log2(${value})`);
                    setLastTerm(`log2(${value})`);
                }
                else {
                    break;
                }
                setValue(math.evaluate(`log2(${value})`));
                setLastInputType('function');
                break;
            case 'log10':
                if (operator === '=') {
                    setPreview(prev => 'log10(' + prev + ')');
                }
                else if (lastInputType === 'function' || lastInputType === 'constant') {
                    replaceLastTerm(`log10(${lastTerm})`);
                }
                else if (lastInputType === 'value') {
                    setPreview(prev => prev + `log10(${value})`);
                    setLastTerm(`log10(${value})`);
                }
                else {
                    break;
                }
                setValue(math.evaluate(`log10(${value})`));
                setLastInputType('function');
                break;
            case 'e^x':
                if (operator === '=') {
                    setPreview(prev => 'e ^ (' + prev + ')');
                }
                else if (lastInputType === 'function') {
                    replaceLastTerm(`e ^ (${lastTerm})`);
                }
                else if (lastInputType === 'constant') {
                    replaceLastTerm(`e ^ ${lastTerm}`);
                }
                else if (lastInputType === 'value') {
                    setPreview(prev => prev + `e ^ ${value}`);
                    setLastTerm(`e ^ ${value}`);
                }
                else {
                    break;
                }
                setValue(math.evaluate(`e^${value}`));
                setLastInputType('function');
                break;
            case 'ln':
                if (operator === '=') {
                    setPreview(prev => 'log(' + prev + ')');
                }
                else if (lastInputType === 'function' || lastInputType === 'constant') {
                    replaceLastTerm(`log(${lastTerm})`);
                }
                else if (lastInputType === 'value') {
                    setPreview(prev => prev + `log(${value})`);
                    setLastTerm(`log(${value})`);
                }
                else {
                    break;
                }
                setValue(math.evaluate(`log(${value})`));
                setLastInputType('function');
                break;
            default:
                break;
        }
    }

    const handleConstant = (input: string) => {
        if (operator === '=') {
            clearAll();
        }
        else if (lastInputType === 'function' || lastInputType === 'constant') {
            removeLastTerm();
        }

        switch(input) {
            case 'Rand':
                const random = String(math.random()).slice(0, 12);
                if (operator === 'root') {
                    let newPreview = preview.slice(0, -1) + random + ')';
                    setPreview(newPreview);
                    setValue(random);
                    setOperator(null);
                    setLastTerm('root' + getLastGroup(newPreview));
                    setLastInputType('function');
                }
                else {
                    // setPreview(prev => prev + 'rand()');
                    setValue(random);
                    setLastTerm('');
                    setLastInputType('value');
                }
                break;
            case 'pi':
                if (operator === 'root') {
                    let newPreview = preview.slice(0, -1) + '$\\pi$)';
                    setPreview(newPreview);
                    setValue(String(math.pi));
                    setOperator(null);
                    setLastTerm('root' + getLastGroup(newPreview));
                    setLastInputType('function');
                }
                else {
                    setPreview(prev => prev + '$\\pi$');
                    setValue(String(math.pi));
                    setLastTerm('$\\pi$');
                    setLastInputType('constant');
                }
                break;
            case 'e':
                if (operator === 'root') {
                    let newPreview = preview.slice(0, -1) + 'e)';
                    setPreview(newPreview);
                    setValue(String(math.e));
                    setOperator(null);
                    setLastTerm('root' + getLastGroup(newPreview));
                    setLastInputType('function');
                }
                else {
                    setPreview(prev => prev + 'e');
                    setValue(String(math.e));
                    setLastTerm('e');
                    setLastInputType('constant');
                }
                break;
            default:
                break;
        }
    }

    const replaceAbsoluteValues = (operation: string) => {
        let expression = operation;
        let firstIndex = expression.indexOf('|');
        let lastIndex = expression.lastIndexOf('|');

        while (firstIndex >= 0 && lastIndex >= 0 && firstIndex < lastIndex) {
            expression = expression.substring(0, firstIndex) + 'abs(' + expression.substring(firstIndex + 1);
            lastIndex = lastIndex + 3;
            expression = expression.substring(0, lastIndex) + ')' + expression.substring(lastIndex + 1);
            firstIndex = expression.indexOf('|');
            lastIndex = expression.lastIndexOf('|');
        }

        return expression;
    }

    const mapOperation = (operation: string) => {
        // let expression = operation.replaceAll(' ', '');
        // expression = replaceAbsoluteValues(expression);

        return replaceAbsoluteValues(operation)
            .replaceAll('$\\pi$', 'pi')
            .replaceAll('root', 'nthRoot')
            .replaceAll('×', '*')
            .replaceAll('÷', '/');
    }

    const compute = () => {
        let operation = preview;

        if (operator === 'root') {
            operation = operation.slice(0, -1) + value + ')';
        }
        else if (lastInputType === 'value') {
            operation += value;
        }
        setPreview(operation);
        setOperator('=');

        try {
            const answer = math.evaluate(mapOperation(operation));

            if (math.isComplex(answer) && answer.im !== 0) {
                setValueHelper('Error');
                setHistory(prev => ([
                    {
                        'preview': operation,
                        'answer': 'Error'
                    },
                    ...prev
                ]));
            }
            else {
                setValueHelper(answer);
                setHistory(prev => ([
                    {
                        'preview': operation,
                        'answer': answer
                    },
                    ...prev
                ]));
            }
        }
        catch {
            setValueHelper('Error');
        }
    }

    const renderKatex = () => {
        const katex = document.getElementsByClassName('katex');
        
        for (let i = 0; i < katex.length; i++) {
            renderMathInElement(katex[i], {
                delimiters: [
                    {left: '$$', right: '$$', display: true},
                    {left: '$', right: '$', display: false},
                    {left: '\\(', right: '\\)', display: false},
                    {left: '\\[', right: '\\]', display: true}
                ],
                trust: true
            });

            katex[i].innerHTML = katex[i].innerHTML.replaceAll('&nbsp;', ' ');
        }

        const math = document.querySelectorAll('.katex-mathml');
        for (let i = 0; i < math.length; i++) {
            math[i].remove();
        }
    }

    useEffect(() => {
        renderKatex();
    }, [mode, preview, shift, history, historyOpen]);

    const digitInput = (
        <div className='calculator__input'>
            <div className='calculator__inputRow'>
                <button className='calculator__inputButton' onClick={memoryClear}>
                    MC
                </button>
                <button className='calculator__inputButton' onClick={memoryAdd}>
                    M+
                </button>
                <button className='calculator__inputButton' onClick={memorySubtract}>
                    M-
                </button>
                <button className='calculator__inputButton' onClick={memoryRecall}>
                    MR
                </button>
            </div>
            <div className='calculator__inputRow'>
                <button className='calculator__inputButton' onClick={backSpace}>
                    <MdArrowRightAlt className='calculator__leftArrow' />
                </button>
                <button className='calculator__inputButton' onClick={clearEntry}>
                    CE
                </button>
                <button className='calculator__inputButton' onClick={clearAll}>
                    C
                </button>
                <button className='calculator__inputButtonOperator' onClick={() => addOperator('×')}>
                    ×
                </button>
            </div>
            <div className='calculator__inputRow'>
                <button className='calculator__inputButton' onClick={() => addValue('7')}>
                    7
                </button>
                <button className='calculator__inputButton' onClick={() => addValue('8')}>
                    8
                </button>
                <button className='calculator__inputButton' onClick={() => addValue('9')}>
                    9
                </button>
                <button className='calculator__inputButtonOperator' onClick={() => addOperator('÷')}>
                    ÷
                </button>
            </div>
            <div className='calculator__inputRow'>
                <button className='calculator__inputButton' onClick={() => addValue('4')}>
                    4
                </button>
                <button className='calculator__inputButton' onClick={() => addValue('5')}>
                    5
                </button>
                <button className='calculator__inputButton' onClick={() => addValue('6')}>
                    6
                </button>
                <button className='calculator__inputButtonOperator' onClick={() => addOperator('-')}>
                    -
                </button>
            </div>
            <div className='calculator__inputRow'>
                <button className='calculator__inputButton' onClick={() => addValue('1')}>
                    1
                </button>
                <button className='calculator__inputButton' onClick={() => addValue('2')}>
                    2
                </button>
                <button className='calculator__inputButton' onClick={() => addValue('3')}>
                    3
                </button>
                <button className='calculator__inputButtonOperator' onClick={() => addOperator('+')}>
                    +
                </button>
            </div>
            <div className='calculator__inputRow'>
                <button className='calculator__inputButton' onClick={() => handleFunction('±')}>
                    ±
                </button>
                <button className='calculator__inputButton' onClick={() => addValue('0')}>
                    0
                </button>
                <button className='calculator__inputButton' onClick={() => addValue('.')}>
                    .
                </button>
                <button className='calculator__inputButtonOperator' onClick={compute}>
                    =
                </button>
            </div>
        </div>
    );

    const functionInput = (
        <div className='calculator__input'>
            <div className='calculator__inputRow'>
                <button className='calculator__inputButton' onClick={() => handleFunction('Abs')}>
                    Abs
                </button>
                <button className='calculator__inputButton' onClick={() => handleConstant('Rand')}>
                    Rand
                </button>
                <button className='calculator__inputButton' onClick={() => setDegreeMode(prev => prev === 'Rad' ? 'Deg' : 'Rad')}>
                    {degreeMode === 'Rad' ? 'Deg' : 'Rad'}
                </button>
                <button className='calculator__inputButton' onClick={() => handleFunction('%')}>
                    %
                </button>
            </div>
            <div className='calculator__inputRow'>
                <button className='calculator__inputButton' onClick={() => handleFunction('(')}>
                    {'('}
                </button>
                <button className='calculator__inputButton' onClick={() => handleFunction(')')}>
                    {')'}
                </button>
                <button className='calculator__inputButton' onClick={() => handleFunction(shift ? '10^x' : '2^x')}>
                    {shift
                        ? <p className='katex'>{'$10^{\\text{x}}$'}</p>
                        : <p className='katex'>{'$2^{\\text{x}}$'}</p>
                    }
                </button>
                <button className='calculator__inputButton' onClick={() => handleFunction(shift ? 'x^3' : 'x^2')}>
                    {shift
                        ? <p className='katex'>{'$\\text{x}^{3}$'}</p>
                        : <p className='katex'>{'$\\text{x}^{2}$'}</p>
                    }
                </button>
            </div>
            <div className='calculator__inputRow'>
                <button className='calculator__inputButton' onClick={() => addOperator(shift ? 'root' : '^')}>
                    {shift
                        ? <p className='katex'>{'$\\sqrt[\\text{x}]{\\text{y}}$'}</p>
                        // ? <p>{'x√y'}</p>
                        : <p className='katex'>{'$\\text{y}^{\\text{x}}$'}</p>
                    }
                    
                </button>
                <button className='calculator__inputButton' onClick={() => handleFunction('1/x')}>
                    <p className='katex'>{'$\\frac{1}{\\text{x}}$'}</p>
                </button>
                <button className='calculator__inputButton' onClick={() => handleFunction('x!')}>
                    x!
                </button>
                <button className='calculator__inputButton' onClick={() => handleFunction(shift ? 'cbrt' : 'sqrt')}>
                    {shift
                        ? <p className='katex'>{'$\\sqrt[3]{\\text{x}}$'}</p>
                        : <p className='katex'>{'$\\sqrt{\\text{x}}$'}</p>
                    }
                    {/* {shift ? '3√x' : '√x'} */}
                </button>
            </div>
            <div className='calculator__inputRow'>
                <button className='calculator__inputButton' onClick={() => handleFunction(shift ? 'asin' : 'sin')}>
                    {shift
                        ? <p className='katex'>{'$\\text{sin}^{-1}$'}</p>
                        : <p>sin</p>
                    }
                </button>
                <button className='calculator__inputButton' onClick={() => handleFunction(shift ? 'acos' : 'cos')}>
                    {shift
                        ? <p className='katex'>{'$\\text{cos}^{-1}$'}</p>
                        : <p>cos</p>
                    }
                </button>
                <button className='calculator__inputButton' onClick={() => handleFunction(shift ? 'atan' : 'tan')}>
                    {shift
                        ? <p className='katex'>{'$\\text{tan}^{-1}$'}</p>
                        : <p>tan</p>
                    }
                </button>
                <button className='calculator__inputButton' onClick={() => handleFunction('log2')}>
                    <p className='katex'>{'$\\text{log}_{2}$'}</p>
                </button>
            </div>
            <div className='calculator__inputRow'>
                <button className='calculator__inputButton' onClick={() => handleFunction(shift ? 'asinh' : 'sinh')}>
                    {shift
                        ? <p className='katex'>{'$\\text{sinh}^{-1}$'}</p>
                        : <p>sinh</p>
                    }
                </button>
                <button className='calculator__inputButton' onClick={() => handleFunction(shift ? 'acosh' : 'cosh')}>
                    {shift
                        ? <p className='katex'>{'$\\text{cosh}^{-1}$'}</p>
                        : <p>cosh</p>
                    }
                </button>
                <button className='calculator__inputButton' onClick={() => handleFunction(shift ? 'atanh' : 'tanh')}>
                    {shift
                        ? <p className='katex'>{'$\\text{tanh}^{-1}$'}</p>
                        : <p>tanh</p>
                    }
                </button>
                <button className='calculator__inputButton' onClick={() => handleFunction('log10')}>
                    <p className='katex'>{'$\\text{log}_{10}$'}</p>
                </button>
            </div>
            <div className='calculator__inputRow'>
                <button className='calculator__inputButton' onClick={() => handleConstant('pi')}>
                    <p className='katex'>{'$\\pi$'}</p>
                </button>
                <button className='calculator__inputButton' onClick={() => handleConstant('e')}>
                    e
                </button>
                <button className='calculator__inputButton' onClick={() => handleFunction('e^x')}>
                    <p className='katex'>{'$\\text{e}^{\\text{x}}$'}</p>
                </button>
                <button className='calculator__inputButton' onClick={() => handleFunction('ln')}>
                    ln
                </button>
            </div>
        </div>
    );

    return (
        <div className='calculator'>
            <div className='calculator__display'>
                <p className='calculator__degreeMode'>{degreeMode}</p>
                <p className='calculator__value'>{value}{(operator !== '^' && operator !== 'root') && <span> {operator}</span>}</p>
            </div>
            <div className='calculator__preview'>
                <p className='katex'>{preview}</p>
            </div>
            <div className='calculator__actionBar'>
                <button
                    className={historyOpen ? 'calculator__actionButton--active' : 'calculator__actionButton'}
                    onClick={() => setHistoryOpen(prev => !prev)}
                >
                    HISTORY
                </button>
                <button
                    className={shift ? 'calculator__actionButton--active' : 'calculator__actionButton'}
                    onClick={() => {
                        if (mode === 'digits') setMode('functions');
                        else setShift(!shift);
                    }}
                >
                    SHIFT
                </button>
                <button
                    className='calculator__actionButton'
                    onClick={() => setMode(prev => prev === 'digits' ? 'functions' : 'digits')}
                >
                    {mode === 'digits' ? 'FUNCS' : 'DIGITS'}
                </button>
            </div>
            {mode === 'digits'
                ? digitInput
                : mode === 'functions'
                ? functionInput
                : <></>   
            }
            {historyOpen &&
                <div className='calculator__history'>
                    <div className='calculator__historyDisplay'>
                        {history.map(entry => (
                            <div className='calculator__historyEntry'>
                                <p className='katex'>{entry.preview}</p>
                                <p>= {entry.answer}</p>
                            </div>
                        ))}
                    </div>
                    <div className='calculator__historyFooter'>
                        <button className='calculator__historyClear' onClick={() => setHistory([])}>
                            CLEAR
                        </button>
                    </div>
                </div>
            }
        </div>
    );
}

export default ScientificCalculator;